import React, { useState, useEffect, useCallback } from "react";
import vendorAPI from "../api/vendorAPI";
import { debounce } from "lodash";
import VendorModal from "./VendorModal";
import { useAuth } from "../context/AuthContext";
import { useVendorTerms } from "../utils/terminology";

const onlyDigits = (s = "") => (s || "").replace(/\D/g, "");

const formatTinDisplay = (tinRaw) => {
  const digits = onlyDigits(tinRaw);
  // Auto-format when 10 or more digits: XXX-XXX-XXX-(remainder)
  if (digits.length > 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return tinRaw;
};

const formatAddressDisplay = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address;
  if (typeof address === "object") {
    return [address.street, address.city || address.cityMunicipality, address.province, address.zipCode]
      .filter(Boolean)
      .join(", ");
  }
  return "";
};

const VendorSearch = ({
  onSelect,
  value = null,
  placeholder = null,
  required = false,
  disabled = false,
  className = "",
}) => {
  const { isAuthReady, isAuthenticated, isRefreshRevoked } = useAuth();
  const term = useVendorTerms();
  const [searchTerm, setSearchTerm] = useState("");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(value);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [notice, setNotice] = useState("");

  // Load initial vendor if value is provided
  useEffect(() => {
    if (value && typeof value === "object") {
      setSelectedVendor(value);
      setSearchTerm(value.label || value.name || "");
    } else if (!value) {
      setSelectedVendor(null);
      setSearchTerm("");
    }
  }, [value]);

  // Debounced search (vendorAPI.searchVendors returns ARRAY already)
  const searchVendors = useCallback(
    debounce(async (query) => {
      if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
        setVendors([]);
        setShowDropdown(false);
        return;
      }

      if (!query || query.trim().length < 2) {
        setVendors([]);
        return;
      }

      setLoading(true);
      try {
        const list = await vendorAPI.searchVendors(query);
        setVendors(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Error searching vendors:", error);
        setVendors([]);
      } finally {
        setLoading(false);
      }
    }, 250),
    [isAuthReady, isAuthenticated, isRefreshRevoked]
  );

  const handleInputChange = (e) => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    if (selectedVendor) {
      // The box currently shows the stale "Name (TIN: ...)" label from the
      // prior selection (see handleSelectVendor). Editing it in place would
      // send that label text — parens and all — as the search query, which
      // can fail to match on name/code. Clear it instead so the next
      // keystroke starts a fresh search.
      setSelectedVendor(null);
      onSelect?.(null);
      setSearchTerm("");
      setVendors([]);
      setShowDropdown(true);
      return;
    }

    const query = e.target.value;
    setSearchTerm(query);
    setShowDropdown(true);
    searchVendors(query);
  };

  const handleSelectVendor = (vendor) => {
    // Only allow selecting active vendors
    if (vendor.status !== 'active') {
      setNotice(`Cannot select ${vendor.status} ${term.lower}.`);
      return;
    }

    if (selectedVendor?._id && selectedVendor._id === vendor?._id) {
      setNotice(`${term.one} already added.`);
    } else {
      setNotice("");
    }

    setSelectedVendor(vendor);
    setSearchTerm(vendor.label || vendor.name || "");
    setShowDropdown(false);
    setVendors([]);
    onSelect?.(vendor);
  };

  const handleClear = () => {
    setSelectedVendor(null);
    setSearchTerm("");
    setVendors([]);
    setShowDropdown(false);
    setNotice("");
    onSelect?.(null);
  };

  // click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".vendor-search-container")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const showNoResults = showDropdown && searchTerm.trim().length >= 2 && vendors.length === 0 && !loading;

  return (
    <div className={`vendor-search-container relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder ?? `Search ${term.lower} by name, code, or TIN...`}
          required={required}
          disabled={disabled || !isAuthReady || !isAuthenticated || isRefreshRevoked}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
          autoComplete="off"
        />

        {selectedVendor && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Clear selection"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {loading && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {showDropdown && vendors.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {vendors.map((vendor) => {
            const isActive = vendor.status === 'active';
            return (
              <div
                key={vendor._id}
                onClick={() => isActive && handleSelectVendor(vendor)}
                onMouseDown={(e) => e.preventDefault()}
                className={`px-3 py-2 border-b border-gray-100 last:border-b-0 ${
                  isActive
                    ? 'hover:bg-teal-50 cursor-pointer'
                    : 'opacity-60 cursor-not-allowed bg-gray-50'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="font-medium text-gray-900">{vendor.name}</div>
                  {!isActive && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                      {vendor.status === 'inactive' ? 'Inactive' : 'Suspended'}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {vendor.tin && <span className="mr-3">TIN: {formatTinDisplay(vendor.tin)}</span>}
                  {vendor.code && <span>Code: {vendor.code}</span>}
                </div>
                {(() => {
                  const addrStr = formatAddressDisplay(vendor.address);
                  return addrStr ? <div className="text-xs text-gray-500 mt-0.5"> {addrStr}</div> : null;
                })()}
                {vendor.defaultAtcCode2306 && (
                  <div className="text-xs text-teal-600 mt-1">Default ATC: {vendor.defaultAtcCode2306}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No results + create */}
      {showNoResults && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 space-y-2">
          <div className="text-gray-500 text-center">No {term.lowerMany} found for "{searchTerm}"</div>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowCreateModal(true)}
            className="w-full text-sm text-teal-700 font-medium hover:underline"
          >
            + Create new vendor
          </button>
        </div>
      )}

      {/* Selected vendor details */}
      {selectedVendor && (
        <div className="mt-2 p-3 bg-teal-50 rounded-md border border-teal-200">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-medium text-teal-800">{selectedVendor.name}</div>
              <div className="text-sm text-teal-600">
                {selectedVendor.tin && (
                  <div>
                    TIN: <strong>{formatTinDisplay(selectedVendor.tin)}</strong>
                  </div>
                )}
                {selectedVendor.code && <div>Code: {selectedVendor.code}</div>}
                {(() => {
                  const addrStr = formatAddressDisplay(selectedVendor.address);
                  return addrStr ? <div className="text-xs text-gray-500 mt-1"> {addrStr}</div> : null;
                })()}
                {/*selectedVendor.defaultAtcCode2306 && <div>Default ATC (2306): {selectedVendor.defaultAtcCode2306}</div>}
                {selectedVendor.defaultWithholdingRate2306 != null && (
                  <div>Default Rate (2306): {selectedVendor.defaultWithholdingRate2306}%</div>
                )*/}
              </div>
            </div>
            <button type="button" onClick={handleClear} className="text-xs text-teal-600 hover:text-teal-800">
              Change
            </button>
          </div>
        </div>
      )}

      {notice && (
        <div className="mt-2 p-2 rounded-md border border-green-200 bg-green-50 text-green-700 text-sm">
          {notice}
        </div>
      )}

      {/* Create modal */}
      <VendorModal
        isOpen={showCreateModal}
        initialName={searchTerm}
        onClose={() => setShowCreateModal(false)}
        onCreated={(vendor) => {
          const alreadySelected = selectedVendor?._id && selectedVendor._id === vendor?._id;
          setShowCreateModal(false);
          setNotice(alreadySelected ? `${term.one} already added.` : `${term.one} added successfully.`);
          handleSelectVendor(vendor);
        }}
      />
    </div>
  );
};

export default VendorSearch;
